import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

interface BreadcrumbContextValue {
  titles: Record<string, string>;
  setTitle: (segment: string, title: string) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  titles: {},
  setTitle: () => {},
});

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [titles, setTitles] = useState<Record<string, string>>({});

  const setTitle = useCallback((segment: string, title: string) => {
    setTitles(prev => (prev[segment] === title ? prev : { ...prev, [segment]: title }));
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ titles, setTitle }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumb() {
  return useContext(BreadcrumbContext);
}

/**
 * Call inside a page component to register a human-readable label
 * for a URL segment (e.g. replace an Apollo ID with the lead's name).
 */
export function useRegisterBreadcrumbTitle(segment: string | undefined, title: string | undefined) {
  const { setTitle } = useBreadcrumb();
  useEffect(() => {
    if (segment && title) setTitle(segment, title);
  }, [segment, title, setTitle]);
}
